'use client';

import clsx from 'clsx';
import { DataTestConfig } from '@headless-workspace/config';
import {
    GlowButton,
    GlowSkeleton,
    GlowTitle,
    Icons,
    PropsWithFontFamily,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { VipBrandsValue } from '../../../../../server';
import { useProductModalDisplay } from '../../../products';
import { useProductBasketCTABusinessRules } from '../../delivery';

type SelectorAddToBasketProps = PropsWithLocale &
    PropsWithFontFamily & {
        sku: string;
        hasShortLabel: boolean;
        vipBrands: VipBrandsValue;
        isVisible?: boolean;
    };

export const SelectorAddToBasketButton: Component<SelectorAddToBasketProps> = ({
    sku,
    hasShortLabel,
    vipBrands,
    locale,
    fontFamily,
    isVisible = false,
}) => {
    const {
        isLoading: isBasketCTALoading,
        cta: basketCTA,
        isAddingToBasket,
    } = useProductBasketCTABusinessRules({
        hasShortLabel,
    });

    const { displayBasketModal, isBasketModalOpen } = useProductModalDisplay({ vipBrands, locale });

    const ctaClickHandler = async () => {
        if (!basketCTA || !basketCTA.onClick || !basketCTA.actionStatus) {
            return null;
        }

        const productAddedToBasket = await basketCTA.onClick();

        if (!productAddedToBasket) {
            // FIXME: handle error with toast notification in FHEAD-792
            return;
        }

        if (!isBasketModalOpen) {
            displayBasketModal(basketCTA.actionStatus, productAddedToBasket, [sku]);
        }
    };

    if (isBasketCTALoading) {
        return <GlowSkeleton containerClassName={clsx('flex-1 w-full', !isVisible && 'desktopS:max-w-[11.938rem]')} />;
    }

    if (!basketCTA) {
        // Should never happen
        return null;
    }

    if (basketCTA.variant) {
        return (
            <GlowButton
                dataTestId={DataTestConfig.testIds.addToBasketButton}
                className={clsx('flex-1', !isVisible && 'desktopS:max-w-[11.938rem]')}
                Icon={basketCTA.iconName && Icons[basketCTA.iconName]}
                label={basketCTA.label}
                onClick={ctaClickHandler}
                isSpinnerActive={isAddingToBasket}
                variant={basketCTA.variant}
                size={'large'}
                fontFamily={fontFamily}
            />
        );
    }

    return <GlowTitle text={basketCTA.label} fontSize={'title4'} className={'text-text-warning py-0.75 flex-1'} />;
};
